import React, {
  useRef,
  useState,
  useMemo,
  useEffect,
  useCallback,
} from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Stack, Typography, Button, Skeleton, useTheme } from '@mui/material';
import { Box } from '@mui/system';
import { observer } from 'mobx-react-lite';
import format from 'date-fns/format';

import { Container } from '@/components/layout';
import pressCenterService from '@/services/pressCenterService';

const SKELETON_ITEMS = [1, 2, 3];

function AnnouncementItem({
  slug,
  date,
  image,
  title,
}: {
  key: string;
  slug: string;
  date: string;
  title: string;

  image?: {
    url: string;
    alt: string;
  } | null;
}) {
  const theme = useTheme();

  const href = slug ? `/resource/${slug}` : '#';

  const dateString = useMemo(() => format(new Date(date), 'MMMM dd, yyyy'), []);

  return (
    <Link href={href} passHref>
      <Stack
        sx={{
          width: { xs: '100%', sm: '400px' },
          cursor: 'pointer',
          overflow: 'hidden',
          borderRadius: theme.spacing(2),
          backgroundColor: theme.palette.grey[100],
        }}
      >
        <Box width="auto" height={theme.spacing(45)} position="relative">
          {!!image && (
            <Image
              src={image.url}
              alt={image.alt}
              layout="fill"
              objectFit="cover"
              objectPosition="center"
            />
          )}
        </Box>

        <Stack
          sx={{
            gap: theme.spacing(2),
            padding: theme.spacing(3.5, 6, 4.5),
          }}
        >
          <Typography
            fontSize="1.125rem !important"
            lineHeight="1.46 !important"
            variant="h3"
            color="#52545F" 
          >
            {title}
          </Typography>

          <Typography
            fontSize="0.875rem !important"
            lineHeight="1.4 !important"
            variant="body2"
            color="#82848E"
          >
            {dateString}
          </Typography>
        </Stack>
      </Stack>
    </Link>
  );
}

function Announcements() {
  const theme = useTheme();

  const announcementsRef = useRef<Array<Announcement>>([]);
  const [visibleAnnouncements, setVisibleAnnouncements] = useState<
    Array<Announcement>
  >([]);

  const [status, setStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');
  const isLoading = status === 'loading';

  const canShowSeeAllButton =
    announcementsRef.current?.length > 3 &&
    visibleAnnouncements?.length !== announcementsRef.current?.length;

  useEffect(() => {
    setStatus('loading');
    pressCenterService
      .fetchAnnouncements()
      .then((response) => {
        announcementsRef.current = response?.results;

        setStatus('success');
        setVisibleAnnouncements(announcementsRef.current.slice(0, 3));
      })
      .catch(() => {
        setStatus('error');
      });
  }, []);

  const handleSeeAllPress = useCallback(() => {
    setVisibleAnnouncements(announcementsRef.current);
  }, []);

  return (
    <Stack
      sx={{
        gap:{xs: theme.spacing(6), md: theme.spacing(9) },
        paddingTop: { xs: theme.spacing(8), md: theme.spacing(7) },
        paddingBottom: { xs: theme.spacing(11), md: theme.spacing(10) },
        backgroundColor: theme.palette.common.white,
      }}
    >
      <Typography
        fontSize="2.375rem !important"
        lineHeight="1.2 !important"
        variant="h2"
        color="#52545F"
        textAlign="center"
      >
        Announcements
      </Typography>

      <Container>
        <Stack
          sx={{
            flexDirection: { xs: 'column', sm: 'row' },
            flexWrap: 'wrap',
            rowGap: { xs: theme.spacing(6), md: theme.spacing(8)},
            columnGap: theme.spacing(6),
            justifyContent: visibleAnnouncements.length < 3 ? 'flex-start' : 'center',
          }}
        >
          {isLoading
            ? SKELETON_ITEMS.map((index) => (
                <Skeleton
                  key={index}
                  variant="rectangular"
                  sx={{
                    width: { xs: '100%', sm: '400px' },
                    height: theme.spacing(73),
                    borderRadius: theme.spacing(2),
                    backgroundColor: theme.palette.grey[100],
                  }}
                />
              ))
            : visibleAnnouncements.map((item) => (
                <AnnouncementItem
                  key={item.id}
                  slug={item.slug}
                  date={item.updatedAt}
                  title={item.title}
                  image={item.image}
                />
              ))}
        </Stack>
      </Container>

      {canShowSeeAllButton && (
        <Container>
          <Button
            sx={{
              color: '#34353E',
              width: '100%',
              height: theme.spacing(12.75),
              borderRadius: theme.spacing(2),
              backgroundColor: theme.palette.grey[100],
            }}
            onClick={handleSeeAllPress}
          >
            See All
          </Button>
        </Container>
      )}
    </Stack>
  );
}

export default observer(Announcements);
